import { __ } from '@wordpress/i18n';
import { useBlockProps, InspectorControls } from '@wordpress/block-editor';
import {
	PanelBody,
	SelectControl,
	TextControl,
	ToggleControl,
	RangeControl,
	Button,
	Dropdown,
	ColorPicker,
	ColorIndicator,
	BaseControl,
} from '@wordpress/components';
import ServerSideRender from '@wordpress/server-side-render';
import './editor.scss';

/**
 * Reusable compact color control component.
 * Renders a labeled row with a small color indicator
 * that opens a color picker dropdown on click.
 *
 * @param {Object}   props            - Component props.
 * @param {string}   props.label      - The label text for the control.
 * @param {string}   props.colorValue - The current color value.
 * @param {Function} props.onChange   - Callback when the color changes.
 */
const ColorControl = ( { label, colorValue, onChange } ) => (
	<BaseControl className="bbpc-color-control">
		<div className="bbpc-color-control__row">
			<span className="bbpc-color-control__label">{ label }</span>
			<Dropdown
				className="bbpc-color-dropdown"
				contentClassName="bbpc-color-popover"
				popoverProps={ { placement: 'left-start' } }
				renderToggle={ ( { isOpen, onToggle } ) => (
					<Button
						onClick={ onToggle }
						aria-expanded={ isOpen }
						className="bbpc-color-indicator-btn"
					>
						<ColorIndicator
							colorValue={ colorValue || 'transparent' }
						/>
					</Button>
				) }
				renderContent={ () => (
					<ColorPicker
						color={ colorValue }
						onChange={ onChange }
						enableAlpha
					/>
				) }
			/>
		</div>
	</BaseControl>
);

export default function Edit( { attributes, setAttributes } ) {
	const blockProps = useBlockProps();
	const {
		placeholder,
		submit_btn_type,
		submit_btn_text,
		submit_btn_align,
		is_keywords,
		keywords_label,
		keywords_align,
		keywords,
		search_post_type,
		is_ajax_search,
		search_highlight,
		color_text,
		color_placeholder,
		input_bg_color,
		input_border_color,
		input_border_width,
		input_border_radius,
		input_focus_border_color,
		input_height,
		wrapper_max_width,
		color_icon,
		search_bg,
		btn_hover_color,
		btn_hover_bg,
		btn_border_radius,
		color_keywords_label,
		bbpc_color_keywords,
		bbpc_color_keywords_bg,
		bbpc_color_keywords_hover,
		bbpc_color_keywords_bg_hover,
		results_bg_color,
		results_border_color,
		results_text_color,
		results_hover_bg,
		results_border_radius,
		highlight_bg_color,
		highlight_text_color,
	} = attributes;

	/* Check if Forumax Pro is active. */
	const isProActive =
		window.bbpc_upsell_config?.is_pro_active === '1' ||
		window.bbpc_upsell_config?.is_pro_active === true;
	const upgradeUrl = window.bbpc_upsell_config?.upgrade_url;

	/**
	 * Add a new keyword to the keywords array.
	 */
	const addKeyword = () => {
		const newKeywords = [ ...( keywords || [] ), { title: '' } ];
		setAttributes( { keywords: newKeywords } );
	};

	/**
	 * Update a keyword at a specific index.
	 *
	 * @param {number} index - The index of the keyword to update.
	 * @param {string} value - The new title value.
	 */
	const updateKeyword = ( index, value ) => {
		const newKeywords = [ ...( keywords || [] ) ];
		newKeywords[ index ] = { title: value };
		setAttributes( { keywords: newKeywords } );
	};

	/**
	 * Remove a keyword at a specific index.
	 *
	 * @param {number} index - The index of the keyword to remove.
	 */
	const removeKeyword = ( index ) => {
		const newKeywords = [ ...( keywords || [] ) ];
		newKeywords.splice( index, 1 );
		setAttributes( { keywords: newKeywords } );
	};

	return (
		<div { ...blockProps }>
			{ /* ========== Settings Tab ========== */ }
			<InspectorControls>
				{ /* Form Settings Panel */ }
				<PanelBody
					title={ __( 'Form', 'bbp-core' ) }
					initialOpen={ false }
				>
					<TextControl
						label={ __( 'Placeholder', 'bbp-core' ) }
						value={ placeholder }
						onChange={ ( value ) =>
							setAttributes( { placeholder: value } )
						}
					/>
					<div className={ ! isProActive ? 'bbpc-pro-gated' : '' }>
						{ ! isProActive && (
							<span className="bbpc-pro-watermark">PRO</span>
						) }
						<SelectControl
							label={ __( 'Search Post Type', 'bbp-core' ) }
							value={ isProActive ? search_post_type : 'forum' }
							options={ [
								{
									label: __( 'Documentation', 'bbp-core' ),
									value: 'docs',
								},
								{
									label: __( 'Topics', 'bbp-core' ),
									value: 'topic',
								},
								{
									label: __( 'Forums', 'bbp-core' ),
									value: 'forum',
								},
								{
									label: __( 'Posts', 'bbp-core' ),
									value: 'post',
								},
								{
									label: __( 'All', 'bbp-core' ),
									value: '',
								},
							] }
							onChange={ ( value ) => {
								if ( isProActive ) {
									setAttributes( { search_post_type: value } );
								}
							} }
							disabled={ ! isProActive }
						/>
					</div>
					<div className={ ! isProActive ? 'bbpc-pro-gated' : '' }>
						{ ! isProActive && (
							<span className="bbpc-pro-watermark">PRO</span>
						) }
						<ToggleControl
							label={ __( 'AJAX Live Search', 'bbp-core' ) }
							help={ is_ajax_search
								? __( 'Search results will load dynamically as you type.', 'bbp-core' )
								: __( 'Enable to show live search results without page reload.', 'bbp-core' )
							}
							checked={ isProActive ? is_ajax_search : false }
							onChange={ ( value ) => {
								if ( isProActive ) {
									setAttributes( { is_ajax_search: value } );
								}
							} }
							disabled={ ! isProActive }
						/>
					</div>
					<div className={ ! isProActive ? 'bbpc-pro-gated' : '' }>
						{ ! isProActive && (
							<span className="bbpc-pro-watermark">PRO</span>
						) }
						<ToggleControl
							label={ __( 'Highlight Search Keywords', 'bbp-core' ) }
							help={ search_highlight
								? __( 'Matching keywords will be highlighted in results.', 'bbp-core' )
								: __( 'Enable to highlight matching keywords in search results.', 'bbp-core' )
							}
							checked={ isProActive ? search_highlight : false }
							onChange={ ( value ) => {
								if ( isProActive ) {
									setAttributes( { search_highlight: value } );
								}
							} }
							disabled={ ! isProActive }
						/>
					</div>
					<SelectControl
						label={ __( 'Button Type', 'bbp-core' ) }
						value={ submit_btn_type }
						options={ [
							{
								label: __( 'Icon', 'bbp-core' ),
								value: 'icon',
							},
							{
								label: __( 'Text', 'bbp-core' ),
								value: 'text',
							},
						] }
						onChange={ ( value ) =>
							setAttributes( { submit_btn_type: value } )
						}
					/>
					{ submit_btn_type === 'text' && (
						<TextControl
							label={ __( 'Submit Button Text', 'bbp-core' ) }
							value={ submit_btn_text }
							onChange={ ( value ) =>
								setAttributes( { submit_btn_text: value } )
							}
						/>
					) }
					<SelectControl
						label={ __( 'Button Alignment', 'bbp-core' ) }
						value={ submit_btn_align }
						options={ [
							{
								label: __( 'Left', 'bbp-core' ),
								value: 'left',
							},
							{
								label: __( 'Right', 'bbp-core' ),
								value: 'right',
							},
						] }
						onChange={ ( value ) =>
							setAttributes( { submit_btn_align: value } )
						}
					/>
					<div className={ ! isProActive ? 'bbpc-pro-gated' : '' }>
						{ ! isProActive && (
							<span className="bbpc-pro-watermark">PRO</span>
						) }
						<ToggleControl
							label={ __( 'Keywords', 'bbp-core' ) }
							checked={ isProActive ? is_keywords : false }
							onChange={ ( value ) => {
								if ( isProActive ) {
									setAttributes( { is_keywords: value } );
								}
							} }
							disabled={ ! isProActive }
						/>
					</div>
					{ is_keywords && isProActive && (
						<>
							<TextControl
								label={ __( 'Keywords Label', 'bbp-core' ) }
								value={ keywords_label }
								onChange={ ( value ) =>
									setAttributes( { keywords_label: value } )
								}
							/>
							<SelectControl
								label={ __(
									'Keywords Alignment',
									'bbp-core'
								) }
								value={ keywords_align }
								options={ [
									{
										label: __( 'Left', 'bbp-core' ),
										value: 'left',
									},
									{
										label: __( 'Center', 'bbp-core' ),
										value: 'center',
									},
									{
										label: __( 'Right', 'bbp-core' ),
										value: 'right',
									},
								] }
								onChange={ ( value ) =>
									setAttributes( { keywords_align: value } )
								}
							/>
							{ /* Keywords Repeater */ }
							<div className="bbpc-keywords-repeater">
								<p className="components-base-control__label">
									{ __( 'Keywords', 'bbp-core' ) }
								</p>
								{ ( keywords || [] ).map(
									( keyword, index ) => (
										<div
											key={ index }
											className="bbpc-keyword-item"
										>
											<TextControl
												value={ keyword.title }
												onChange={ ( value ) =>
													updateKeyword(
														index,
														value
													)
												}
												placeholder={ __(
													'Enter keyword…',
													'bbp-core'
												) }
											/>
											<Button
												isDestructive
												isSmall
												icon="no-alt"
												onClick={ () =>
													removeKeyword( index )
												}
												label={ __(
													'Remove',
													'bbp-core'
												) }
											/>
										</div>
									)
								) }
								<Button
									variant="secondary"
									isSmall
									onClick={ addKeyword }
									icon="plus"
								>
									{ __( 'Add Keyword', 'bbp-core' ) }
								</Button>
							</div>
						</>
					) }
				</PanelBody>

				{ /* Layout Panel */ }
				<PanelBody
					title={ __( 'Layout', 'bbp-core' ) }
					initialOpen={ false }
				>
					<div className={ ! isProActive ? 'bbpc-pro-gated' : '' }>
						{ ! isProActive && (
							<span className="bbpc-pro-watermark">PRO</span>
						) }
						<RangeControl
							label={ __( 'Max Width (px)', 'bbp-core' ) }
							value={ isProActive ? wrapper_max_width : undefined }
							onChange={ ( value ) => {
								if ( isProActive ) {
									setAttributes( { wrapper_max_width: value } );
								}
							} }
							min={ 200 }
							max={ 1200 }
							allowReset
							disabled={ ! isProActive }
						/>
					</div>
				</PanelBody>
			</InspectorControls>

			{ /* ========== Styles Tab ========== */ }
			<InspectorControls group="styles">
				{ /* --- Input Field (dimensions + colors) --- */ }
				<PanelBody
					title={ __( 'Input Field', 'bbp-core' ) }
					initialOpen={ false }
				>
					<RangeControl
						label={ __( 'Border Radius (px)', 'bbp-core' ) }
						value={ input_border_radius }
						onChange={ ( value ) =>
							setAttributes( { input_border_radius: value } )
						}
						min={ 0 }
						max={ 100 }
						allowReset
					/>
					<RangeControl
						label={ __( 'Border Width (px)', 'bbp-core' ) }
						value={ input_border_width }
						onChange={ ( value ) =>
							setAttributes( { input_border_width: value } )
						}
						min={ 0 }
						max={ 10 }
						allowReset
					/>
					<RangeControl
						label={ __( 'Height (px)', 'bbp-core' ) }
						value={ input_height }
						onChange={ ( value ) =>
							setAttributes( { input_height: value } )
						}
						min={ 30 }
						max={ 100 }
						allowReset
					/>

					<div className="bbpc-color-controls-group">
						<ColorControl
							label={ __( 'Text Color', 'bbp-core' ) }
							colorValue={ color_text }
							onChange={ ( value ) =>
								setAttributes( { color_text: value } )
							}
						/>
						<ColorControl
							label={ __( 'Placeholder Color', 'bbp-core' ) }
							colorValue={ color_placeholder }
							onChange={ ( value ) =>
								setAttributes( {
									color_placeholder: value,
								} )
							}
						/>
						<ColorControl
							label={ __(
								'Background Color',
								'bbp-core'
							) }
							colorValue={ input_bg_color }
							onChange={ ( value ) =>
								setAttributes( { input_bg_color: value } )
							}
						/>
						<ColorControl
							label={ __( 'Border Color', 'bbp-core' ) }
							colorValue={ input_border_color }
							onChange={ ( value ) =>
								setAttributes( {
									input_border_color: value,
								} )
							}
						/>
						<ColorControl
							label={ __(
								'Focus Border Color',
								'bbp-core'
							) }
							colorValue={ input_focus_border_color }
							onChange={ ( value ) =>
								setAttributes( {
									input_focus_border_color: value,
								} )
							}
						/>
					</div>
				</PanelBody>

				{ /* --- Button (dimensions + colors) --- */ }
				<PanelBody
					title={ __( 'Button', 'bbp-core' ) }
					initialOpen={ false }
				>
					<RangeControl
						label={ __( 'Border Radius (px)', 'bbp-core' ) }
						value={ btn_border_radius }
						onChange={ ( value ) =>
							setAttributes( { btn_border_radius: value } )
						}
						min={ 0 }
						max={ 100 }
						allowReset
					/>

					<div className="bbpc-color-controls-group">
						<ColorControl
							label={ __( 'Color', 'bbp-core' ) }
							colorValue={ color_icon }
							onChange={ ( value ) =>
								setAttributes( { color_icon: value } )
							}
						/>
						<ColorControl
							label={ __( 'Background', 'bbp-core' ) }
							colorValue={ search_bg }
							onChange={ ( value ) =>
								setAttributes( { search_bg: value } )
							}
						/>
						<ColorControl
							label={ __( 'Hover Color', 'bbp-core' ) }
							colorValue={ btn_hover_color }
							onChange={ ( value ) =>
								setAttributes( {
									btn_hover_color: value,
								} )
							}
						/>
						<ColorControl
							label={ __(
								'Hover Background',
								'bbp-core'
							) }
							colorValue={ btn_hover_bg }
							onChange={ ( value ) =>
								setAttributes( { btn_hover_bg: value } )
							}
						/>
					</div>
				</PanelBody>

				{ /* --- Keywords (colors only) --- */ }
				<PanelBody
					title={ __( 'Keywords', 'bbp-core' ) }
					initialOpen={ false }
				>
					<div className="bbpc-color-controls-group">
						<ColorControl
							label={ __( 'Label Color', 'bbp-core' ) }
							colorValue={ color_keywords_label }
							onChange={ ( value ) =>
								setAttributes( {
									color_keywords_label: value,
								} )
							}
						/>
						<ColorControl
							label={ __( 'Keyword Color', 'bbp-core' ) }
							colorValue={ bbpc_color_keywords }
							onChange={ ( value ) =>
								setAttributes( {
									bbpc_color_keywords: value,
								} )
							}
						/>
						<ColorControl
							label={ __(
								'Keyword Background',
								'bbp-core'
							) }
							colorValue={ bbpc_color_keywords_bg }
							onChange={ ( value ) =>
								setAttributes( {
									bbpc_color_keywords_bg: value,
								} )
							}
						/>
						<ColorControl
							label={ __(
								'Keyword Hover Color',
								'bbp-core'
							) }
							colorValue={ bbpc_color_keywords_hover }
							onChange={ ( value ) =>
								setAttributes( {
									bbpc_color_keywords_hover: value,
								} )
							}
						/>
						<ColorControl
							label={ __(
								'Keyword Hover Background',
								'bbp-core'
							) }
							colorValue={ bbpc_color_keywords_bg_hover }
							onChange={ ( value ) =>
								setAttributes( {
									bbpc_color_keywords_bg_hover: value,
								} )
							}
						/>
					</div>
				</PanelBody>

				{ /* --- Search Results (dimensions + colors) --- */ }
				<PanelBody
					title={ __( 'Search Results', 'bbp-core' ) }
					initialOpen={ false }
				>
					<RangeControl
						label={ __( 'Border Radius (px)', 'bbp-core' ) }
						value={ results_border_radius }
						onChange={ ( value ) =>
							setAttributes( {
								results_border_radius: value,
							} )
						}
						min={ 0 }
						max={ 50 }
						allowReset
					/>

					<div className="bbpc-color-controls-group">
						<ColorControl
							label={ __(
								'Background Color',
								'bbp-core'
							) }
							colorValue={ results_bg_color }
							onChange={ ( value ) =>
								setAttributes( {
									results_bg_color: value,
								} )
							}
						/>
						<ColorControl
							label={ __( 'Border Color', 'bbp-core' ) }
							colorValue={ results_border_color }
							onChange={ ( value ) =>
								setAttributes( {
									results_border_color: value,
								} )
							}
						/>
						<ColorControl
							label={ __( 'Text Color', 'bbp-core' ) }
							colorValue={ results_text_color }
							onChange={ ( value ) =>
								setAttributes( {
									results_text_color: value,
								} )
							}
						/>
						<ColorControl
							label={ __(
								'Hover Background',
								'bbp-core'
							) }
							colorValue={ results_hover_bg }
							onChange={ ( value ) =>
								setAttributes( {
									results_hover_bg: value,
								} )
							}
						/>
					</div>
					{ search_highlight && (
						<div className="bbpc-color-controls-group">
							<p className="components-base-control__label">
								{ __( 'Highlight', 'bbp-core' ) }
							</p>
							<ColorControl
								label={ __(
									'Highlight Background',
									'bbp-core'
								) }
								colorValue={ highlight_bg_color }
								onChange={ ( value ) =>
									setAttributes( {
										highlight_bg_color: value,
									} )
								}
							/>
							<ColorControl
								label={ __(
									'Highlight Text Color',
									'bbp-core'
								) }
								colorValue={ highlight_text_color }
								onChange={ ( value ) =>
									setAttributes( {
										highlight_text_color: value,
									} )
								}
							/>
						</div>
					) }
				</PanelBody>
			</InspectorControls>

			<ServerSideRender
				block="bbp-core/search"
				attributes={ attributes }
			/>
		</div>
	);
}
